import React, { useEffect, useState } from "react";
import { FiSearch, FiFilter, FiTrash2 } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { getAllClientsAction } from "store/features/auth/authActions";
import {
  selectAllClients,
  selectAllClientsLoading,
} from "store/features/auth/authSelectors";
import avatar from "assets/img/avatars/avatar.png";


const User = () => {
  const dispatch = useDispatch();
  const allClients = useSelector(selectAllClients);
  const loading = useSelector(selectAllClientsLoading);

  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    dispatch(getAllClientsAction());
  }, [dispatch]);

  const transformClientData = (clients) => {
    if (!clients) return [];
    return clients.map((client) => ({
      id: client._id,
      name: client.displayName || "New User",
      email: client.email || "N/A",
      phone: client.mobileNo || "N/A",
      avatar: client.image,
      joined: new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      }).format(new Date(client.memberJoiningDate)),
      status: "Active",
    }));
  };

  const filteredUsers = transformClientData(allClients).filter((user) =>
    Object.values(user).some(
      (value) =>
        typeof value === "string" &&
        value.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  
  if (loading)
    return (
      <div className="flex h-screen items-center justify-center">
        Loading users...
      </div>
    );

  return (
    <div className="container mx-auto min-h-screen px-4 py-8">
      <div className="mb-6 flex flex-col items-start justify-between md:flex-row md:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
            User Management
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            Manage all registered users in the system
          </p>
        </div>

        <div className="mt-4 flex items-center space-x-3 md:mt-0">
          <div className="relative w-full md:w-64">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <FiSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search users..."
              className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <button className="rounded-lg border border-gray-300 p-2 text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-navy-600">
            <FiFilter size={18} />
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-md dark:bg-navy-700">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
            <thead className="bg-navy-600 dark:bg-navy-600">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-200 dark:text-gray-300">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-200 dark:text-gray-300">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-200 dark:text-gray-300">
                  Phone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-200 dark:text-gray-300">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-200 dark:text-gray-300">
                  Joined Date
                </th>
               
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-600 dark:bg-navy-700">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-sm">
                    {searchTerm ? (
                      <>
                        No users found matching your search
                        <button
                          onClick={() => setSearchTerm("")}
                          className="ml-2 text-blue-600 hover:text-blue-800 dark:text-blue-400"
                        >
                          Clear search
                        </button>
                      </>
                    ) : (
                      "No users available"
                    )}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr
                    key={user.id}
                    className="hover:bg-gray-50 dark:hover:bg-navy-600"
                  >
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                      <div className="flex items-center">
                        <img
                          src={user.avatar || avatar}
                          alt={user.name}
                          className="mr-3 h-8 w-8 rounded-full"
                        />
                        <span>{user.name}</span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-gray-300">
                      {user.email}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-gray-300">
                      {user.phone}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm">
                      <span
                        className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                          user.status === "Active"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {user.status}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-gray-300">
                      {user.joined}
                    </td>
                 
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default User;
